import { ExecuteAll } from 'grb-webui/services/Promise';
import RepositoryUploader from './RepositoryUploader';
import {
  StartValidateFileSet,
  ValidateFileInSet,
  StopValidateFileSet,
  getFileExtensions,
  MODEL_EXTENSIONS,
} from './BatchFileValidator';

function validateResultFile(file, ctx) {
  if (file.type.ext === 'json' && !ctx.exts.attr) {
    return 'If specifying a \'JSON\' file as a solution, an \'ATTR\' file should be provided as an input';
  }
  return undefined;
}

const getFileComprExt = (file) => {
  return file.compr === 'none' ? '' : `.${file.compr}`;
};

class BatchUploader extends RepositoryUploader {
  constructor(props) {
    super(Object.assign({}, props, { shared: false }));
    this.batchService = props.batchService;
    this.setDefaultProps();

    const fileSetValidator = {
      startValidate: StartValidateFileSet,
      validateFile: (meta, ctx) => {
        // Validate file to upload
        return ValidateFileInSet(meta.name, ctx);
      },
      stopValidate: (ctx) => {
        // continue to validate repository objects
        const { repoNodes } = this;
        let changed = false;
        repoNodes.forEach((node) => {
          const error = ValidateFileInSet(node.object.name, ctx);
          if (error !== node.validationError) {
            node.validationError = error;
            changed = true;
          }
        });
        if (changed) {
          ctx.repoNodesChanged = true;
        }
        // Validate result files
        changed = false;
        this.resultFiles.forEach((file) => {
          const error = validateResultFile(file, ctx);
          if (error !== file.validationError) {
            changed = true;
            file.validationError = error;
          }
        });
        if (changed) {
          ctx.resultFilesChanged = true;
          this.resultFiles = this.resultFiles.slice();
        }
        StopValidateFileSet(ctx);
      },
    };
    this.addValidator(fileSetValidator);
  }

  setDefaultProps() {
    this.batchId = null;
    this.priority = 0;
    this.group = '';
    this.app = '';
    this.resultFiles = [];
    this.repoNodes = [];
    this.parameters = [];
  }

  reset() {
    return super.reset()
    .then(() => this.batchId && this.apiClient.delete(`/batches/${this.batchId}`))
    .then(() => {
      this.setDefaultProps();
    });
  }

  prepareUpload(nodesToUpload, ctx) {
    super.prepareUpload(nodesToUpload, ctx);
    this.repoNodes.forEach((node) => {
      if (node.error) {
        node.error = null;
      }
    });
  }

  uploadStarted(ctx) {
    const { priority, group, app } = this;
    const batchCreate = { priority, group, appName: app };
    if (this.getResultFiles().length) {
      batchCreate.resultFiles = this.getResultFiles().map(file => `solution.${file.type.ext}${getFileComprExt(file)}`);
    }
    if (this.getParameters().length) {
      batchCreate.parameters = {};
      this.getParameters().forEach((p) => {
        batchCreate.parameters[p.name] = p.value;
      });
    }
    const uploader = this;
    return this.batchService.createBatch(batchCreate)
          .then((batchId) => {
            uploader.batchId = batchId;
            return Object.assign(ctx, { batchId, container: `batch/${batchId}` });
          });
  }

  uploadEnded() {
    const { batchService } = this;
    const uploader = this;
    const objectsToAttach = this.getUploadNodesToAttach()
        .map(node => ({ storeId: node.id, name: node.name, node }))
        .concat(uploader.getRepositoryNodesToAttach().map(node => ({ storeId: node.object.id, name: node.object.name, node })));
    return ExecuteAll(
      objectsToAttach.map((oa) => {
        return batchService.attachObjectToBatch(uploader.batchId, { storeId: oa.storeId, name: oa.name })
                .then(() => { const { node } = oa; node.batchId = uploader.batchId; });
                // .catch((err) => { const { node } = oa; node.error = FormatError(err); throw err; });
      }),
    )
    .then(() => {
      return batchService.closeBatch(uploader.batchId).then(() => {
        const { batchId } = uploader;
        uploader.batchId = undefined;
        return batchId;
      });
    })
    .then((batchId) => {
      return batchService.restartBatchesPolling().then(() => {
        return uploader.reset().then(() => batchId);
      });
    });
  }

  resetDoneStates() {
    super.resetDoneStates();
    const removeBatchId = (n) => {
      const node = n;
      node.batchId = null;
      return node;
    };
    this.fileNodes.forEach(removeBatchId);
    this.repoNodes.forEach(removeBatchId);
  }

  uploadFailed(err, uploadCtx) {
    return super.uploadFailed(err, uploadCtx)
      .then(() => {
        this.resetDoneStates(); // To be able to upload again resources that were successfuly uploaded
        if (this.batchId) {
          return this.batchService.deleteBatch(this.batchId)
            .then(() => { this.batchId = null; return err; });
        }
        return err;
      });
  }

  getUploadNodesToAttach() {
    const nodes = this.fileNodes.filter(node => node.isDone() && !node.batchId);
    let modelIndex = -1;
    const modelNode = nodes.find((node, i) => {
      const [, ext] = getFileExtensions(node.name);
      if (MODEL_EXTENSIONS.indexOf(ext) >= 0) {
        modelIndex = i;
        return true;
      }
      return false;
    });
    if (modelIndex > 0) {
      nodes.splice(modelIndex, 1);
      nodes.unshift(modelNode);
    }
    return nodes;
  }

  getRepositoryNodesToAttach() {
    return this.repoNodes.filter(node => !node.batchId);
  }

  getRepositoryNodes() {
    return this.repoNodes;
  }

  addRepositoryObject(object) {
    this.repoNodes = this.repoNodes.concat({ object });
    return this.repoNodes;
  }

  getRepositoryNodeIndex(object) {
    const nodes = this.repoNodes;
    for (let i = 0; i < nodes.length; i += 1) {
      if ((nodes[i] === object) || (nodes[i].object.id === object.id)) {
        return i;
      }
    }
    return -1;
  }

  removeRepositoryObject(object) {
    const index = this.getRepositoryNodeIndex(object);
    if (index >= 0) {
      this.repoNodes = this.repoNodes.slice();
      this.repoNodes.splice(index, 1);
      return this.repoNodes;
    }
    return null;
  }

  getResultFiles() {
    return this.resultFiles;
  }

  setResultFiles(files) {
    this.resultFiles = files;
    return this.resultFiles;
  }

  addResultFiles(files) {
    this.resultFiles = this.resultFiles.concat(files);
    return this.resultFiles;
  }

  removeResultFile(file) {
    const index = this.resultFiles.indexOf(file);
    const newResultFiles = this.resultFiles.slice();
    newResultFiles.splice(index, 1);
    this.resultFiles = newResultFiles;
    return newResultFiles;
  }

  changeResultFile(file, prop, value) {
    const newFile = Object.assign({}, file, { [prop]: value });
    const index = this.resultFiles.indexOf(file);
    const newResultFiles = this.resultFiles.slice();
    newResultFiles[index] = newFile;
    this.resultFiles = newResultFiles;
    return newResultFiles;
  }

  setPriority(priority) {
    this.priority = priority;
  }

  getPriority() {
    return this.priority;
  }

  getGroup() {
    return this.group;
  }

  setGroup(group) {
    this.group = group;
  }

  getApplication() {
    return this.app;
  }

  setApplication(app) {
    this.app = app;
  }

  getParameters() {
    return this.parameters;
  }

  setParameters(params) {
    this.parameters = params;
    return this.validateParameters();
  }

  addParameter(name, value) {
    this.parameters.push({ name, value });
    return this.validateParameters(true);
  }

  changeParameterName(index, name) {
    this.parameters[index].name = name;
    return this.validateParameters(true);
  }

  changeParameterValue(index, value) {
    this.parameters[index].value = value;
    return this.validateParameters(true);
  }

  deleteParameter(index) {
    this.parameters.splice(index, 1);
    return this.validateParameters(true);
  }

  validateParameters(changed) {
    const usedNames = {};
    let errorChanged = false;
    this.parameters.forEach((p) => {
      const param = p;
      let newError;
      if (!param.name) {
        newError = 'Empty parameter name';
      } else if (usedNames[param.name]) {
        newError = `Parameter ${param.name} already used`;
      } else {
        usedNames[param.name] = true;
        newError = undefined;
      }
      if (newError !== param.nameError) {
        errorChanged = true;
        param.nameError = newError;
      }
      newError = !param.value ? 'Empty parameter value' : undefined;
      if (newError !== param.valueError) {
        errorChanged = true;
        param.valueError = newError;
      }
    });
    if (changed || errorChanged) {
      this.parameters = this.parameters.slice();
    }
    return this.parameters;
  }
}

export default BatchUploader;
